import { useState } from 'react';
import { Button, Card, Col, Form, Modal, Row, Stack } from 'react-bootstrap';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import CustomModal from 'src/components/custom_components/CustomModal/CustomModal';

import {
  DiabetesDataLabelEnum,
  LengthOfDiabetesDiagnosisEnum,
  TimeSinceLastEyeExamEnum,
  TypeOfDiabetesEnum,
} from 'src/models/DiabetesData.model';
import { I18NextNS } from 'src/models/Language.model';

import useSubmitSession from 'src/hooks/useSubmitSession';

const DiabetesData = () => {
  const { t } = useTranslation(I18NextNS.PAGES);
  const history = useHistory();

  const { session, updateSession } = useRemoteBoundStore();
  const { updateDiabetesData } = useSubmitSession();

  const { handleSubmit, register } = useForm({
    defaultValues: {
      typeOfDiabetes: TypeOfDiabetesEnum.Other,
      lengthOfDiabetesDiagnosis: LengthOfDiabetesDiagnosisEnum.Unknown,
      timeSinceLastEyeExam: TimeSinceLastEyeExamEnum.Unknown,
    },
  });

  const submitDiabetesData: SubmitHandler<{
    typeOfDiabetes: TypeOfDiabetesEnum;
    lengthOfDiabetesDiagnosis: LengthOfDiabetesDiagnosisEnum;
    timeSinceLastEyeExam: TimeSinceLastEyeExamEnum;
  }> = (data) => {
    updateDiabetesData({
      typeOfDiabetes: data.typeOfDiabetes,
      lengthOfDiabetesDiagnosis: data.lengthOfDiabetesDiagnosis,
      timeSinceLastEyeExam: data.timeSinceLastEyeExam,
    });
    updateSession({
      typeOfDiabetes: data.typeOfDiabetes,
      lengthOfDiabetesDiagnosis: data.lengthOfDiabetesDiagnosis,
      timeSinceLastEyeExam: data.timeSinceLastEyeExam,
    });

    setIsShowConfirmModal(true);
  };

  const [isShowConfirmModal, setIsShowConfirmModal] = useState(false);

  const handleCloseConfirmModal = () => {
    setIsShowConfirmModal(false);
  };

  return (
    <Stack gap={4} className="h-100">
      <Row>
        <Col md={6}>
          <div>
            <p className="mb-0">{t('diabetesData.title')}</p>
            <h3>
              {session.firstName} {session.lastName}
            </h3>
          </div>
        </Col>
      </Row>
      <Row className="h-100">
        <Form
          className="d-flex flex-column justify-content-between"
          onSubmit={handleSubmit(submitDiabetesData)}
        >
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>{DiabetesDataLabelEnum.TypeOfDiabetes}</Form.Label>
                <Form.Select {...register('typeOfDiabetes')}>
                  {Object.values(TypeOfDiabetesEnum).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>
                  {DiabetesDataLabelEnum.LengthOfDiabetesDiagnosis}
                </Form.Label>
                <Form.Select {...register('lengthOfDiabetesDiagnosis')}>
                  {Object.values(LengthOfDiabetesDiagnosisEnum).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>
                  {DiabetesDataLabelEnum.TimeSinceLastEyeExam}
                </Form.Label>
                <Form.Select {...register('timeSinceLastEyeExam')}>
                  {Object.values(TimeSinceLastEyeExamEnum).map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          <Stack
            direction="horizontal"
            className="justify-content-end mb-5"
            gap={2}
          >
            <Button variant="primary-100" onClick={() => history.goBack()}>
              {t('diabetesData.navigation.previous')}
            </Button>

            <Button variant="primary" className="w-200px shadow" type="submit">
              {t('diabetesData.navigation.next')}
            </Button>
          </Stack>
        </Form>
      </Row>

      <CustomModal
        show={isShowConfirmModal}
        onHide={handleCloseConfirmModal}
        scrollable={true}
        fullscreen="xl-down"
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('diabetesData.confirmModal.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Card>
            <Card.Body>
              <Stack gap={2}>
                <Row>
                  <Col>
                    <span className="fw-medium">
                      {DiabetesDataLabelEnum.TypeOfDiabetes}
                    </span>
                  </Col>
                  <Col>
                    <span>{session.typeOfDiabetes}</span>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <span className="fw-medium">
                      {DiabetesDataLabelEnum.LengthOfDiabetesDiagnosis}
                    </span>
                  </Col>
                  <Col>
                    <span>{session.lengthOfDiabetesDiagnosis}</span>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <span className="fw-medium">
                      {DiabetesDataLabelEnum.TimeSinceLastEyeExam}
                    </span>
                  </Col>
                  <Col>
                    <span>{session.timeSinceLastEyeExam}</span>
                  </Col>
                </Row>
              </Stack>
            </Card.Body>
          </Card>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="primary-100" onClick={handleCloseConfirmModal}>
            {t('diabetesData.confirmModal.cancel')}
          </Button>

          <Button
            variant="primary"
            className="shadow"
            onClick={() => history.push('/session-hint')}
          >
            {t('diabetesData.confirmModal.confirm')}
          </Button>
        </Modal.Footer>
      </CustomModal>
    </Stack>
  );
};

export default DiabetesData;
